import { useState, useEffect } from "react";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  ExternalLink,
  RefreshCw,
  PieChart,
  ArrowDownCircle,
  ArrowUpCircle,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getRecentTransactions } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import {
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";

interface User {
  uid: string;
  email: string;
  wallet: string;
  username: string;
  joinedAt: string;
}

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
}

const RECEIVE_LIMIT = 100;
const TRANSFER_LIMIT = 100;

const AnalyticsPage = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    Transaction[]
  >([]);
  const [totalReceived, setTotalReceived] = useState<number>(0);
  const [totalSent, setTotalSent] = useState<number>(0);
  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});
  const [loadingTransactions, setLoadingTransactions] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const [periodName, setPeriodName] = useState<string>("");

  const user = useSelector((state: RootState) => state.user) as User | null;
  const walletAddress = user?.wallet || "";

  useEffect(() => {
    const now = new Date();
    if (period === "monthly") {
      setPeriodName(
        `${now.toLocaleString("default", {
          month: "long",
        })} ${now.getFullYear()}`
      );
    } else {
      setPeriodName(`${now.getFullYear()}`);
    }
  }, [period]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!walletAddress) {
        setError("No wallet address found for the user.");
        toast.error("No wallet address found for the user.");
        return;
      }

      setLoadingTransactions(true);
      try {
        const txs = await getRecentTransactions(walletAddress);
        console.log("Fetched transactions:", txs);
        setTransactions(txs);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching transactions:", err.message);
        toast.error("Failed to fetch transactions. Please try again.");
        setError("Failed to fetch transactions: " + err.message);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [walletAddress]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const map: { [address: string]: string | undefined } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.username;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.name;
          }
        });

        setAddressToUsername(map);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      }
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    const now = new Date();
    const start = period === "monthly" ? startOfMonth(now) : startOfYear(now);
    const end = period === "monthly" ? endOfMonth(now) : endOfYear(now);

    const filtered = transactions.filter((tx) => {
      try {
        const txDate = parse(tx.date, "M/d/yyyy, h:mm:ss a", new Date());
        if (!isValid(txDate)) {
          console.warn(
            `Invalid date format for transaction ${tx.id}: ${tx.date}`
          );
          return false;
        }
        console.log(
          `Transaction ${tx.id} date: ${
            tx.date
          }, parsed: ${txDate.toISOString()}`
        );
        return txDate >= start && txDate <= end;
      } catch (e) {
        console.warn(
          `Error parsing date for transaction ${tx.id}: ${tx.date}`,
          e
        );
        return false;
      }
    });

    console.log(`Filtered ${period} transactions:`, filtered);
    setFilteredTransactions(filtered);

    const received = filtered.reduce(
      (sum, tx) => (tx.type === "received" ? sum + tx.amount : sum),
      0
    );
    const sent = filtered.reduce(
      (sum, tx) => (tx.type === "sent" ? sum + tx.amount : sum),
      0
    );

    setTotalReceived(received);
    setTotalSent(sent);
  }, [transactions, period]);

  const remainingReceive = Math.max(0, RECEIVE_LIMIT - totalReceived);
  const remainingTransfer = Math.max(0, TRANSFER_LIMIT - totalSent);

  const receiveUsedPercent = Math.min(
    100,
    (totalReceived / RECEIVE_LIMIT) * 100
  );
  const transferUsedPercent = Math.min(100, (totalSent / TRANSFER_LIMIT) * 100);

  const totalFlow = totalReceived + totalSent;
  const receivedDeg = totalFlow > 0 ? (totalReceived / totalFlow) * 360 : 0;

  const pieStyle =
    totalFlow > 0
      ? {
          background: `conic-gradient(#61CE70 0deg ${receivedDeg}deg, #EF4444 ${receivedDeg}deg 360deg)`,
        }
      : { background: "#374151" };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleRefresh = async () => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      toast.error("No wallet address found for the user.");
      return;
    }

    setLoadingTransactions(true);
    try {
      const txs = await getRecentTransactions(walletAddress);
      console.log("Refreshed transactions:", txs);
      setTransactions(txs);
      setError(null);
      toast.success("Transactions refreshed!");
    } catch (err: any) {
      console.error("Error refreshing transactions:", err.message);
      toast.error("Failed to refresh transactions.");
      setError("Failed to refresh transactions: " + err.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || truncateAddress(counterparty);
  };

  const viewOnBlockExplorer = (hash: string) => {
    window.open(`https://basescan.org/tx/${hash}`, "_blank");
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4 sm:p-0">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          {period === "monthly" ? "Monthly Analytics" : "Yearly Analytics"}
        </h1>
        <p className="text-gray-400 text-lg">
          Overview of your wallet activity for {periodName}
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setPeriod("monthly")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            period === "monthly"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setPeriod("yearly")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            period === "yearly"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Yearly
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-gradient-to-br from-[#61CE70]/20 to-[#0F1419] border border-[#61CE70]/30 rounded-3xl p-5 sm:p-6 shadow-lg shadow-[#61CE70]/10 hover:shadow-[#61CE70]/20 transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <ArrowDownCircle className="w-10 h-10 text-[#61CE70] transform hover:scale-110 transition-transform" />
              <h3 className="text-lg sm:text-xl font-semibold text-white">
                Incoming Activity
              </h3>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Total Received</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#61CE70]">
                {totalReceived.toFixed(2)} USDC
              </p>
            </div>
            {period === "monthly" && (
              <div>
                <p className="text-sm text-gray-400">Receiving Limit</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {remainingReceive.toFixed(2)} / {RECEIVE_LIMIT} USDC
                </p>
                <div className="mt-3 w-full bg-gray-800 h-3 rounded-full overflow-hidden">
                  <div
                    className="bg-[#61CE70] h-3 rounded-full transition-all duration-500"
                    style={{ width: `${receiveUsedPercent}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  {receiveUsedPercent.toFixed(0)}% used
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500/20 to-[#0F1419] border border-red-500/30 rounded-3xl p-5 sm:p-6 shadow-lg shadow-red-500/10 hover:shadow-red-500/20 transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <ArrowUpCircle className="w-10 h-10 text-red-400 transform hover:scale-110 transition-transform" />
              <h3 className="text-lg sm:text-xl font-semibold text-white">
                Outgoing Activity
              </h3>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Total Sent</p>
              <p className="text-2xl sm:text-3xl font-bold text-red-400">
                {totalSent.toFixed(2)} USDC
              </p>
            </div>
            {period === "monthly" && (
              <div>
                <p className="text-sm text-gray-400">Transfer Limit</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {remainingTransfer.toFixed(2)} / {TRANSFER_LIMIT} USDC
                </p>
                <div className="mt-3 w-full bg-gray-800 h-3 rounded-full overflow-hidden">
                  <div
                    className="bg-red-400 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${transferUsedPercent}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  {transferUsedPercent.toFixed(0)}% used
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction Flow Pie Chart */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <PieChart className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">
              Transaction Flow Distribution
            </h3>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div
            className="w-48 h-48 sm:w-64 sm:h-64 rounded-full shadow-lg"
            style={pieStyle}
          ></div>
          <div className="flex justify-center space-x-6 mt-6">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-[#61CE70] rounded-full mr-2"></div>
              <span className="text-gray-300">
                Incoming ({totalReceived.toFixed(2)} USDC)
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-[#EF4444] rounded-full mr-2"></div>
              <span className="text-gray-300">
                Outgoing ({totalSent.toFixed(2)} USDC)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">
            Transactions for {periodName}
          </h3>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-[#61CE70]/20 rounded-xl hover:bg-[#61CE70]/30 transition-colors text-[#61CE70]"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>

        {loadingTransactions ? (
          <div className="text-center text-gray-400">
            Loading transactions...
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center text-gray-400">
            No transactions found for {periodName}.
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {filteredTransactions.map((tx) => (
              <div
                key={tx.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                  <div
                    className={`p-3 rounded-xl shrink-0 ${
                      tx.type === "received"
                        ? "bg-[#61CE70]/20"
                        : "bg-red-500/20"
                    }`}
                  >
                    {tx.type === "received" ? (
                      <ArrowDownLeft className="w-5 h-5 text-[#61CE70]" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium capitalize truncate">
                      {tx.type === "received" ? "Deposit" : "Transfer"}
                    </p>
                    <p className="text-gray-400 text-sm break-words">
                      {tx.type === "received" ? (
                        <span>
                          From:{" "}
                          <span className="font-bold text-white uppercase">
                            {getCounterpartyDisplay(tx)}
                          </span>
                        </span>
                      ) : (
                        <span>
                          To:{" "}
                          <span className="font-bold text-white uppercase">
                            {getCounterpartyDisplay(tx)}
                          </span>
                        </span>
                      )}
                    </p>
                    <p className="text-gray-500 text-xs">{tx.date}</p>
                  </div>
                </div>
                <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                  <p
                    className={`text-base sm:text-lg font-bold ${
                      tx.type === "received" ? "text-[#61CE70]" : "text-red-400"
                    }`}
                  >
                    {tx.type === "received" ? "+" : "-"}
                    {tx.amount.toFixed(2)} USDC
                  </p>
                  <div className="flex flex-wrap sm:justify-end gap-2">
                    <button
                      onClick={() => copyToClipboard(tx.hash)}
                      className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                    >
                      <span className="hidden sm:inline">
                        {truncateAddress(tx.hash)}
                      </span>
                      <span className="sm:hidden">Hash</span>
                      <Copy className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => viewOnBlockExplorer(tx.hash)}
                      className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                    >
                      <span>Explorer</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
